<script setup>
import {
  computed,
  ref,
  inject,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  useId,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineCard from './PipelineCard.vue';
import PipelineFilter from './PipelineFilter.vue';
import Modal from '../../../../components/Modal.vue';
import StageColorPicker from './StageColorPicker.vue';
import MarkAsWonModal from './MarkAsWonModal.vue';
import MarkAsLostModal from './MarkAsLostModal.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import { isSet } from 'dashboard/helper/filterHelper';
import { emitter } from 'shared/helpers/mitt';
import {
  matchesColumnFilters,
  getCardCurrency,
  getCardValue,
  getCardPriority,
} from '../helpers/columnFilterMatchers';
import { dismissMenuOnFocusOut } from '../helpers/menuDismissal';
import { formatLocalizedCurrency } from '../utils/localeFormatting';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import { getStageColorClasses } from './pipelineTemplates';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: '#64748b',
  },
  items: {
    type: Array,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  canMovePrevious: {
    type: Boolean,
    default: false,
  },
  canMoveNext: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits([
  'add',
  'edit',
  'delete',
  'drop',
  'itemClick',
  'itemsUpdated',
  'cardStageChanged',
  'hideToggle',
  'reorder',
]);

// Note: Filters are now loaded in onMounted() with user-scoped keys
// to ensure user isolation when switching between accounts

const { t, locale } = useI18n();

const store = useStore();
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const { canManagePipeline } = usePipelinePermissions(selectedPipeline);

// Ciclo 9 (a11y) — prefixo único por instância para os ids dos controles dos
// modais de ordenação/edição e do menu de opções: o componente monta uma vez
// POR COLUNA, então ids fixos duplicariam no DOM e quebrariam label↔campo.
const uid = useId();
const fieldId = suffix => `${uid}-${suffix}`;

// User-scoped localStorage key prefix to isolate data between users
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
let stageSaveGeneration = 0;
const normalizedActiveAccountId = () => {
  const accountId = store.getters.getCurrentAccountId;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const currentAccount = computed(
  () =>
    store.getters['accounts/getAccount']?.(currentAccountId.value) ||
    store.getters.getCurrentAccount
);
const reportingTimezone = computed(
  () =>
    currentAccount.value?.reporting_timezone ||
    currentAccount.value?.settings?.reporting_timezone ||
    'UTC'
);
const accountCurrency = computed(() =>
  getCardCurrency(
    {
      currency: currentAccount.value?.currency,
      item_details: { currency: currentAccount.value?.settings?.currency },
    },
    'BRL'
  )
);
const userScopedKeyPrefix = computed(
  () => `u${currentUserId.value}_a${currentAccountId.value}_`
);

// Verificar se etapas ganho/perdido estão habilitadas
const enableWonLostStages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  return pipeline?.settings?.enable_won_lost_stages !== false;
});

// Adicionar estado para filtros
const showFilterModal = ref(false);
// Initialize columnFilters - will be loaded from localStorage in onMounted()
const columnFilters = ref(null);
const showOptionsMenu = ref(false);
const optionsPopoverRef = ref(null);
const optionsButtonRef = ref(null);
const optionsMenuRef = ref(null);
watch(showOptionsMenu, isOpen => {
  if (!isOpen) optionsPopoverRef.value?.hide();
});
const columnSettings = ref({
  showWon: true,
  showLost: true,
  showAll: true,
  hideColumn: false,
});
const showEditStageModal = ref(false);
const editingStageForm = ref({
  name: '',
  color: '',
  description: '',
});
// Adicionar ref para controle do modal de ordenação
const showSortModal = ref(false);
const sortSnapshot = ref(null);
const columnSort = ref({
  // Default: ordem manual (position). Mantém os cards onde o usuário os
  // soltou no drag-and-drop e reflete o reorder em tempo real. `position`
  // é NOT NULL (default via set_default_position = max+1 por stage), então
  // a ordenação é estável; cards novos são anexados ao fim do estágio.
  type: 'position',
  direction: 'asc',
});
// Colapso em massa: injeta o ref reativo provido pelo PipelineView (fonte
// única que os cards leem) + setter em lote. O computed derivado
// (allItemsCollapsed) é declarado após filteredItems.
const collapsedItemsShared = inject('collapsedItems', ref({}));
const updateCollapsedItems = inject('updateCollapsedItems', () => {});

// Refs para Lazy Loading
const itemsToShowCount = ref(6); // Mostrar 6 itens inicialmente
const itemsIncrement = 10; // Carregar mais 10 a cada vez
const scrollContainer = ref(null); // Ref para o container de scroll

// Scroll indicator state
const canScrollDown = ref(false);
// Simétrico do de baixo: sem ele, quem já rolou a etapa não tem pista de que
// existe conteúdo acima — o topo da lista fica idêntico ao início dela.
const canScrollUp = ref(false);
const updateScrollIndicator = () => {
  if (!scrollContainer.value) return;
  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  canScrollDown.value = scrollHeight - scrollTop - clientHeight > 20;
  canScrollUp.value = scrollTop > 20;
};

// Drag & Drop state
const isDragOver = ref(false);

// Opções de ordenação disponíveis
const sortOptions = computed(() => [
  { id: 'position', label: t('PIPELINE.SORT_OPTIONS.POSITION') },
  { id: 'created_at', label: t('PIPELINE.SORT_OPTIONS.CREATED_AT') },
  { id: 'title', label: t('PIPELINE.SORT_LABELS.TITLE') },
  { id: 'priority', label: t('PIPELINE.SORT_LABELS.PRIORITY') },
  { id: 'value', label: t('PIPELINE.SORT_LABELS.VALUE') },
  { id: 'next_activity', label: t('PIPELINE.SORT_OPTIONS.NEXT_ACTIVITY') },
  { id: 'last_activity', label: t('PIPELINE.SORT_OPTIONS.LAST_ACTIVITY') },
  {
    id: 'conversation_activity',
    label: t('PIPELINE.SORT_OPTIONS.CONVERSATION_ACTIVITY'),
  },
]);
const sortSelectOptions = computed(() =>
  sortOptions.value.map(option => ({
    value: option.id,
    label: option.label,
  }))
);

// Storage keys with user-scoped prefix to isolate data between users
const SETTINGS_STORAGE_KEY = computed(
  () => `${userScopedKeyPrefix.value}pipeline_column_${props.id}_settings`
);
const SORT_STORAGE_KEY = computed(
  () => `${userScopedKeyPrefix.value}pipeline_column_${props.id}_sort`
);
const APPLIED_FILTER_STORAGE_KEY = computed(
  () =>
    `${userScopedKeyPrefix.value}pipeline_column_${props.id}_applied_filters`
);
// COLLAPSED_ITEMS_STORAGE_KEY removida — o colapso em massa passa pelo
// setter em lote do PipelineView (dono do localStorage user-scoped).
const SCROLL_STATE_STORAGE_KEY = computed(
  () => `${userScopedKeyPrefix.value}pipeline_column_${props.id}_scroll_state`
);

// Holds scroll state to restore on first items load (used after page refresh)
let scrollStateToRestore = null;

// Fechar menu ao clicar fora
const closeOptionsMenuOnClickOutside = () => {
  if (showOptionsMenu.value) {
    showOptionsMenu.value = false;
    optionsPopoverRef.value?.hide();
  }
};

const closeOptionsMenuOnEscape = event => {
  if (event.key === 'Escape' && showOptionsMenu.value) {
    showOptionsMenu.value = false;
    optionsPopoverRef.value?.hide();
    nextTick(() => optionsButtonRef.value?.focus());
  }
};

const closeOptionsMenuAndRestoreFocus = () => {
  showOptionsMenu.value = false;
  optionsPopoverRef.value?.hide();
  nextTick(() => optionsButtonRef.value?.focus());
};

const closeOptionsMenuAfterFocusLeaves = event => {
  dismissMenuOnFocusOut(event, () => {
    showOptionsMenu.value = false;
    optionsPopoverRef.value?.hide();
  });
};

const getOptionsMenuItems = container => [
  ...container.querySelectorAll(
    'button:not([disabled]), input:not([disabled])'
  ),
];

const focusFirstOptionsMenuItem = () => {
  const [firstItem] = optionsMenuRef.value
    ? getOptionsMenuItems(optionsMenuRef.value)
    : [];
  firstItem?.focus();
};

const handleOptionsMenuKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeOptionsMenuAndRestoreFocus();
    return;
  }

  const items = getOptionsMenuItems(event.currentTarget);
  const currentIndex = items.indexOf(document.activeElement);
  let targetIndex = null;

  if (event.key === 'ArrowDown') targetIndex = currentIndex + 1;
  if (event.key === 'ArrowUp') targetIndex = currentIndex - 1;
  if (event.key === 'Home') targetIndex = 0;
  if (event.key === 'End') targetIndex = items.length - 1;

  if (targetIndex !== null && items.length) {
    event.preventDefault();
    event.stopPropagation();
    items[(targetIndex + items.length) % items.length].focus();
  }
};

// Named handler for emitter to properly remove on unmount
const handleCloseAllDropdowns = () => {
  showOptionsMenu.value = false;
  optionsPopoverRef.value?.hide();
};

// Salvar configurações no localStorage (user-scoped)
const saveColumnSettings = () => {
  localStorage.setItem(
    SETTINGS_STORAGE_KEY.value,
    JSON.stringify(columnSettings.value)
  );
  emit('hideToggle', { id: props.id, hidden: columnSettings.value.hideColumn });
};

// Salvar configurações de ordenação (user-scoped)
const saveSortSettings = () => {
  localStorage.setItem(
    SORT_STORAGE_KEY.value,
    JSON.stringify(columnSort.value)
  );
};

// "Cancelar" desfaz de verdade: restaura o estado de quando o modal abriu e
// regrava, porque cada mexida nos controles já foi persistida.
const cancelSortSettings = () => {
  if (sortSnapshot.value) {
    columnSort.value = { ...sortSnapshot.value };
    saveSortSettings();
  }
  showSortModal.value = false;
};

const filteredItems = computed(() => {
  if (columnSettings.value.hideColumn) {
    return [];
  }

  // 1. Start with the items passed from the parent (already filtered by global search)
  let items = [...props.items];

  // 2. Apply local column filters (priority, value, agent, date).
  // Matching extraído para helpers puros (columnFilterMatchers.js) — corrige
  // a leitura de priority/owner_id na raiz do payload e o valor com
  // semântica server-side (expected_revenue → item_details.value).
  if (columnFilters.value) {
    items = items.filter(item =>
      matchesColumnFilters(item, columnFilters.value, {
        reportingTimezone: reportingTimezone.value,
      })
    );
  }

  // 3. Apply status filters (won/lost) using database fields instead of stale item_details.status
  items = items.filter(item => {
    // Use won_at/lost_at from database instead of item_details.status (which is stale)
    const isWon = Boolean(item.won_at);
    const isLost = Boolean(item.lost_at);
    // Keep items that are not 'won' or 'lost' (active/open items)
    if (!isWon && !isLost) return true;
    // Apply toggles only to 'won' and 'lost' items
    if (isWon && !columnSettings.value.showWon) return false;
    if (isLost && !columnSettings.value.showLost) return false;
    return true; // Item passes status filters
  });
  // 4. Apply sorting
  return items.sort((a, b) => {
    const direction = columnSort.value.direction === 'asc' ? 1 : -1;
    const type = columnSort.value.type;

    let valueA;
    let valueB;

    switch (type) {
      case 'position': {
        // Ordem manual do kanban. position é NOT NULL; usa id como
        // desempate para uma ordenação estável quando posições empatam.
        valueA = a.position ?? 0;
        valueB = b.position ?? 0;
        if (valueA !== valueB) return direction * (valueA - valueB);
        return direction * ((a.id || 0) - (b.id || 0));
      }

      case 'title':
        // item_details.title é mirror JSONB que pode faltar; fallback para o
        // title raiz (coluna do DB) — mesma cadeia do displayTitle.
        valueA = a.item_details?.title || a.title || '';
        valueB = b.item_details?.title || b.title || '';
        return direction * valueA.localeCompare(valueB);

      case 'priority': {
        const priorityOrder = {
          urgent: 4,
          high: 3,
          medium: 2,
          low: 1,
          none: 0, // Ensure 'none' or undefined priority is handled
        };
        // Ciclo 6: priority vive na RAIZ do payload (getCardPriority com
        // fallback legacy em item_details) — antes lia só item_details e
        // ordenava tudo como 'none'.
        valueA = priorityOrder[getCardPriority(a)] ?? 0;
        valueB = priorityOrder[getCardPriority(b)] ?? 0;
        return direction * (valueA - valueB);
      }

      case 'value':
        // Ciclo 6: mesma semântica server-side dos filtros (expected_revenue
        // → item_details.value); cards sem valor ordenam como 0 (comportamento
        // anterior preservado).
        valueA = getCardValue(a) ?? 0;
        valueB = getCardValue(b) ?? 0;
        return direction * (valueA - valueB);

      case 'next_activity':
        // Cards without next activity sort to end (asc) or beginning (desc)
        valueA = a.next_activity_at
          ? new Date(a.next_activity_at)
          : new Date(9999, 0);
        valueB = b.next_activity_at
          ? new Date(b.next_activity_at)
          : new Date(9999, 0);
        if (Number.isNaN(valueA.getTime())) return direction;
        if (Number.isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);

      case 'last_activity':
        // Cards without pipeline activity sort to end (desc) or beginning (asc)
        valueA = a.last_pipeline_activity_at
          ? new Date(a.last_pipeline_activity_at)
          : new Date(0);
        valueB = b.last_pipeline_activity_at
          ? new Date(b.last_pipeline_activity_at)
          : new Date(0);
        if (Number.isNaN(valueA.getTime())) return direction;
        if (Number.isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);

      case 'conversation_activity':
        // Cards without linked conversation sort to end
        valueA = a.conversation_last_activity_at
          ? new Date(a.conversation_last_activity_at)
          : new Date(0);
        valueB = b.conversation_last_activity_at
          ? new Date(b.conversation_last_activity_at)
          : new Date(0);
        if (Number.isNaN(valueA.getTime())) return direction;
        if (Number.isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);

      case 'created_at': // Default sorting
      default:
        valueA = new Date(a.created_at);
        valueB = new Date(b.created_at);
        // Handle potential invalid dates during sorting
        if (Number.isNaN(valueA.getTime())) return direction;
        if (Number.isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);
    }
  });
});
// Lista de itens a serem realmente exibidos com lazy loading
const displayedItems = computed(() => {
  return filteredItems.value.slice(0, itemsToShowCount.value);
});
// Estado de colapso em massa DERIVADO do ref compartilhado — o ref local
// antigo (sempre false no mount) dessincronizava do estado real após
// remount/toggles individuais.
const allItemsCollapsed = computed(
  () =>
    filteredItems.value.length > 0 &&
    filteredItems.value.every(item => !!collapsedItemsShared.value[item.id])
);

// Resultados do filtro para exibir no modal
const filteredResults = computed(() => {
  if (!columnFilters.value) return { total: 0, stages: {} };

  const stages = {};
  stages[props.title] = filteredItems.value.length;

  return {
    total: filteredItems.value.length,
    stages,
  };
});
// Ciclo 6 (2026-06-10): total da coluna com a MESMA semântica de valor dos
// filtros/sort (getCardValue: expected_revenue → item_details.value). Antes
// lia só item_details.value e cards com valor em expected_revenue não
// entravam no total do header (achado QA do ciclo 5).
const columnTotals = computed(() => {
  const totals = {};
  filteredItems.value.forEach(item => {
    const value = getCardValue(item);
    if (value === null) return;
    const currency = getCardCurrency(item, accountCurrency.value);
    totals[currency] = (totals[currency] || 0) + value;
  });
  return totals;
});
const DEFAULT_COLOR = '#64748b';

const isValidHex = color => color && /^#[0-9a-fA-F]{6}$/.test(color);

const safeColor = computed(() =>
  isValidHex(props.color) ? props.color : DEFAULT_COLOR
);
const columnColorClasses = computed(() =>
  getStageColorClasses(safeColor.value)
);

// Deal status modals (won/lost) hosted at the column level so the inline board
// dropdown captura o valor/motivo — alguns funis exigem won_value, então NÃO dá
// pra marcar ganho direto pela API sem input (gerava 422 silencioso/"nada
// acontece"). Reopen não precisa de input → chamada direta.
const showWonModal = ref(false);
const showLostModal = ref(false);
const dealStatusCard = ref(null);

// Aplica o payload PARCIAL de deal_status (id, status, pipeline_stage, won_*/lost_*)
// sobre o card existente no store, sem perder os demais campos, e emite o evento
// de troca de etapa quando o card auto-move para Ganho/Perdido.
const applyDealStatusResult = (card, payload) => {
  if (!payload) return;
  const updatedCard = { ...card, ...payload };
  store.commit('pipelineCards/REPLACE_CARD', updatedCard);
  const oldStage = card.pipeline_stage || props.id;
  if (updatedCard.pipeline_stage && updatedCard.pipeline_stage !== oldStage) {
    // Ganho/Perdido/Reopen movem o card de etapa (→ Ganho/Perdido ou de volta),
    // mas não passam pelo move otimista do drag — sem este ajuste o badge das
    // colunas origem/destino fica defasado até o próximo fetch (QA 2026-06-12).
    store.commit('pipelineCards/ADJUST_STAGE_COUNTS', {
      fromStage: oldStage,
      toStage: updatedCard.pipeline_stage,
    });
    emit('cardStageChanged', {
      card: updatedCard,
      oldStage,
      newStage: updatedCard.pipeline_stage,
    });
  } else {
    emit('itemsUpdated');
  }
};

const handleStatusChange = async ({ card, status }) => {
  // Won/Lost abrem o modal dedicado (captura won_value/motivo via endpoint de
  // deal_status). Reopen vai direto.
  if (status === 'won') {
    dealStatusCard.value = card;
    showWonModal.value = true;
    return;
  }
  if (status === 'lost') {
    dealStatusCard.value = card;
    showLostModal.value = true;
    return;
  }
  try {
    const DealStatusAPI = (await import('dashboard/api/pipeline/dealStatus'))
      .default;
    const response = await DealStatusAPI.reopen(card.id);
    applyDealStatusResult(card, response.data || response);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.STATUS_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message:
        error.response?.data?.error ||
        t('PIPELINE.MESSAGES.STATUS_UPDATED_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Sucesso de um dos modais de deal_status: aplica o payload e fecha.
const handleDealStatusSuccess = payload => {
  if (dealStatusCard.value)
    applyDealStatusResult(dealStatusCard.value, payload);
  showWonModal.value = false;
  showLostModal.value = false;
  dealStatusCard.value = null;
};

// Auto-scroll vertical ao arrastar um card até o topo/base de uma etapa longa.
// Contrapartida do auto-scroll horizontal do board: sem isso, soltar um card
// no fim de uma etapa com dezenas de itens exige rolar antes de arrastar.
// rAF pelo mesmo motivo — `dragover` cala quando o ponteiro fica parado.
const EDGE_SCROLL_ZONE = 64;
const EDGE_SCROLL_MIN_SPEED = 4;
const EDGE_SCROLL_MAX_SPEED = 18;
let edgeScrollFrame = null;
let edgeScrollVelocity = 0;

// `dragend` só chega na coluna de ORIGEM do card. Segurando um card da etapa A
// sobre a borda da etapa B e cancelando com Esc, B não receberia nem `drop` nem
// `dragend` — e seguiria rolando até bater no fim. Enquanto o loop está vivo,
// a coluna escuta o fim do arrasto no documento.
const releaseDragEndGuard = () => {
  document.removeEventListener('dragend', stopEdgeAutoScroll, true); // eslint-disable-line no-use-before-define
  document.removeEventListener('drop', stopEdgeAutoScroll, true); // eslint-disable-line no-use-before-define
};

const stopEdgeAutoScroll = () => {
  edgeScrollVelocity = 0;
  if (edgeScrollFrame !== null) {
    cancelAnimationFrame(edgeScrollFrame);
    edgeScrollFrame = null;
  }
  releaseDragEndGuard();
};

const runEdgeAutoScroll = () => {
  const container = scrollContainer.value;
  if (!container || edgeScrollVelocity === 0) {
    stopEdgeAutoScroll();
    return;
  }
  const before = container.scrollTop;
  container.scrollTop += edgeScrollVelocity;
  // Chegou ao topo/fim: sem esta parada o rAF se reagendava para sempre
  // empurrando um scrollTop que não anda mais — e um drag cancelado com Esc
  // (que não emite `drop`) deixava esse loop vivo indefinidamente.
  if (container.scrollTop === before) {
    stopEdgeAutoScroll();
    return;
  }
  updateScrollIndicator();
  edgeScrollFrame = requestAnimationFrame(runEdgeAutoScroll);
};

const updateEdgeAutoScroll = event => {
  const container = scrollContainer.value;
  if (!container) return;

  const { top, bottom } = container.getBoundingClientRect();
  const distanceFromTop = event.clientY - top;
  const distanceFromBottom = bottom - event.clientY;

  const speedFor = distance => {
    const ratio = Math.min(1, Math.max(0, 1 - distance / EDGE_SCROLL_ZONE));
    return (
      EDGE_SCROLL_MIN_SPEED +
      ratio * (EDGE_SCROLL_MAX_SPEED - EDGE_SCROLL_MIN_SPEED)
    );
  };

  const canScrollUpNow = container.scrollTop > 0;
  const canScrollDownNow =
    container.scrollTop < container.scrollHeight - container.clientHeight - 1;

  if (distanceFromTop < EDGE_SCROLL_ZONE && canScrollUpNow) {
    edgeScrollVelocity = -speedFor(distanceFromTop);
  } else if (distanceFromBottom < EDGE_SCROLL_ZONE && canScrollDownNow) {
    edgeScrollVelocity = speedFor(distanceFromBottom);
  } else {
    stopEdgeAutoScroll();
    return;
  }

  if (edgeScrollFrame === null) {
    document.addEventListener('dragend', stopEdgeAutoScroll, true);
    document.addEventListener('drop', stopEdgeAutoScroll, true);
    edgeScrollFrame = requestAnimationFrame(runEdgeAutoScroll);
  }
};

const handleDrop = event => {
  isDragOver.value = false;
  stopEdgeAutoScroll();
  const itemID = event.dataTransfer?.getData('text/plain');
  if (itemID) {
    const numericId = parseInt(itemID, 10);
    // Garantir que o ID seja passado como número para o componente pai
    emit('drop', {
      itemId: numericId,
      columnId: props.id,
    });
  }
};

const handleDragOver = event => {
  event.preventDefault();
  // `dataTransfer` só é garantido em drags nativos; um dragover sintético
  // (teste, extensão, automação) derrubava o handler antes do auto-scroll.
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
  updateEdgeAutoScroll(event);
};

const handleDragEnter = event => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = event => {
  // Only set to false if leaving the column entirely, not just entering a child
  const relatedTarget = event.relatedTarget;
  if (!event.currentTarget.contains(relatedTarget)) {
    isDragOver.value = false;
    stopEdgeAutoScroll();
  }
};

const hasActiveFilters = computed(() => {
  // Check local column filters (priority, value, agent, date).
  // isSet em vez de truthiness: min/max = 0 é filtro válido — sem isso o
  // emptyStateMessage cai em "Carregando itens..." com filtro-zero ativo.
  const hasColumnFilters =
    columnFilters.value &&
    (columnFilters.value.priority?.length > 0 ||
      isSet(columnFilters.value.value?.min) ||
      isSet(columnFilters.value.value?.max) ||
      columnFilters.value.agent_id ||
      columnFilters.value.date?.start ||
      columnFilters.value.date?.end ||
      columnFilters.value.status ||
      columnFilters.value.stages?.length ||
      columnFilters.value.sla_exceeded ||
      columnFilters.value.labels?.length);

  // Check status toggles (won/lost hidden counts as a filter)
  const hasStatusFilters =
    !columnSettings.value.showWon || !columnSettings.value.showLost;

  return hasColumnFilters || hasStatusFilters;
});

// Total count from backend (all items, not just loaded).
// Usado no badge da coluna, no auto-load e no fallback de "tem mais?".
// meta.stage_counts já vem filtrado server-side pelos filtros do board.
const backendStageCount = computed(() => {
  const meta = store.state.pipelineCards.meta;
  if (meta?.stage_counts && meta.stage_counts[props.id] !== undefined) {
    return meta.stage_counts[props.id];
  }
  return null;
});

// Ciclo 3b — metadado de paginação server-side desta coluna (cursor keyset).
// Populado por fetchBoardByStages/fetchStageCards; null quando o flat foi
// carregado pelo fetch global legado (list/agenda → board).
const stagePagination = computed(
  () => store.getters['pipelineCards/getCardsByStage'][props.id] || null
);

const isStageLoading = computed(() => stagePagination.value?.loading || false);
// Auditoria 2026-08-08 (F22): numa falha parcial do board esta coluna ficava
// em "Carregando itens..." para sempre — o erro global era limpo (para não
// derrubar as colunas boas) e o retry automático não disparava porque
// `hasMore` nasceu falso. Agora a coluna sabe que falhou e oferece saída.
const stageError = computed(() => stagePagination.value?.error || null);

// Tem mais cards no backend? Com estado de stage usa o has_more da última
// página; sem estado (board alimentado pelo fetch global), compara o total
// do backend com o que está carregado localmente.
const stageHasMore = computed(() => {
  if (stagePagination.value) return stagePagination.value.hasMore;
  return (
    backendStageCount.value !== null &&
    props.items.length < backendStageCount.value
  );
});

// Ciclo 9 — total monetário HONESTO sob paginação por coluna. columnTotal
// soma getCardValue só dos cards CARREGADOS (filteredItems ⊂ janela do cursor
// keyset), e o backend NÃO expõe agregado de VALOR por stage no meta (só
// stage_counts). Um drag muda a composição da janela carregada: o card movido
// entra no flat do destino na hora (UPDATE_ITEM_STAGE), mas no reload ele cai
// fora da primeira página do stage (position = max+1 ancora no FIM), então o
// número divergia entre sessão e reload (QA ciclo 8: R$ 1.281.564 vs
// 1.254.941). Enquanto houver mais cards no servidor o total é parcial por
// natureza — sinalizamos com sufixo "+" e tooltip explícito em vez de fingir
// um total exato que nenhuma das duas janelas representa.
const isColumnTotalPartial = computed(() => stageHasMore.value);

const formattedTotal = computed(() => {
  return Object.entries(columnTotals.value)
    .map(([currency, total]) => {
      try {
        return formatLocalizedCurrency(total, {
          currency,
          locale: locale.value,
        });
      } catch {
        return `${currency} ${total}`;
      }
    })
    .join(' · ');
});

const displayedTotal = computed(() => {
  if (!formattedTotal.value) return '';
  return isColumnTotalPartial.value
    ? `${formattedTotal.value}+`
    : formattedTotal.value;
});

// Tooltip do total: count usa filteredItems (o conjunto realmente somado —
// antes usava items.length, ignorando filtros locais e won/lost ocultos).
const columnTotalTooltip = computed(() => {
  const params = {
    count: filteredItems.value.length,
    total: formattedTotal.value,
  };
  return isColumnTotalPartial.value
    ? t('PIPELINE.COLUMN_OPTIONS.TOTAL_VALUE_PARTIAL_TOOLTIP', params)
    : t('PIPELINE.COLUMN_OPTIONS.TOTAL_VALUE_TOOLTIP', params);
});

// Salvar posição de scroll e paginação local (user-scoped)
// Não salva quando filtros estão ativos para evitar restauração inconsistente
const saveScrollState = () => {
  if (hasActiveFilters.value || !scrollContainer.value) return;
  localStorage.setItem(
    SCROLL_STATE_STORAGE_KEY.value,
    JSON.stringify({
      itemsToShowCount: itemsToShowCount.value,
      scrollTop: scrollContainer.value.scrollTop,
    })
  );
};

// Função para carregar mais itens (lazy loading local)
const loadMoreItems = () => {
  if (itemsToShowCount.value < filteredItems.value.length) {
    itemsToShowCount.value = Math.min(
      itemsToShowCount.value + itemsIncrement,
      filteredItems.value.length
    );
    saveScrollState();
  }
};

// Ciclo 3b — "carregar mais" da coluna usa paginação server-side POR STAGE
// (cursor keyset via fetchStageCards), substituindo o append global por
// offset que misturava stages e tinha teto silencioso em 500 cards. O fetch
// por stage não aciona o skeleton (loading global), então o scroll do
// usuário é preservado; o merge no flat dedupa por id.
const loadMoreFromAPI = async () => {
  const pipelineId = store.getters['pipelines/getSelectedPipeline']?.id;
  if (!pipelineId || !stageHasMore.value) return;
  if (isStageLoading.value || store.state.pipelineCards.loading) return;

  await store.dispatch('pipelineCards/fetchStageCards', {
    pipelineId,
    stageId: props.id,
    append: true,
  });
};

// Handler do evento de scroll para lazy loading
const handleScroll = () => {
  if (!scrollContainer.value) return;

  updateScrollIndicator();
  saveScrollState();

  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  const threshold = 150; // Carregar quando faltar 150px para o fim

  if (scrollTop + clientHeight >= scrollHeight - threshold) {
    if (itemsToShowCount.value < filteredItems.value.length) {
      // Still have local items to show
      loadMoreItems();
    } else {
      // All local items shown, try loading more from backend
      loadMoreFromAPI();
    }
  }
};

// Funções para o filtro de coluna
const handleFilter = () => {
  showFilterModal.value = true;
};

const handleFilterApply = appliedFilters => {
  // Check if filters are effectively empty/cleared
  const isCleared =
    !appliedFilters ||
    ((!appliedFilters.priority || appliedFilters.priority.length === 0) &&
      (!appliedFilters.value ||
        (appliedFilters.value.min === null &&
          appliedFilters.value.max === null)) &&
      !appliedFilters.agent_id &&
      (!appliedFilters.date ||
        (!appliedFilters.date.start && !appliedFilters.date.end)) &&
      !appliedFilters.status &&
      (!appliedFilters.stages || appliedFilters.stages.length === 0) &&
      !appliedFilters.sla_exceeded &&
      (!appliedFilters.labels || appliedFilters.labels.length === 0));

  if (isCleared) {
    columnFilters.value = null;
    localStorage.removeItem(APPLIED_FILTER_STORAGE_KEY.value);
  } else {
    columnFilters.value = appliedFilters;
    localStorage.setItem(
      APPLIED_FILTER_STORAGE_KEY.value,
      JSON.stringify(appliedFilters)
    );
  }
  // Clear saved scroll state — filter change resets paginação
  localStorage.removeItem(SCROLL_STATE_STORAGE_KEY.value);
  scrollStateToRestore = null;
  showFilterModal.value = false;
  // Reset lazy load count on filter change
  itemsToShowCount.value = 6;
  nextTick(() => {
    handleScroll();
  }); // Check if scroll needs adjustment
};

// Ciclo 3b — botão "carregar mais": soma o restante local (reveal por slice)
// com o restante no servidor (total do backend - carregado), para o label
// refletir o que realmente falta, não só a página em memória.
const remainingCount = computed(() => {
  const localRemaining = filteredItems.value.length - itemsToShowCount.value;
  const remoteRemaining =
    backendStageCount.value !== null
      ? backendStageCount.value - filteredItems.value.length
      : 0;
  return Math.max(localRemaining, 0) + Math.max(remoteRemaining, 0);
});

const showLoadMoreButton = computed(
  () =>
    itemsToShowCount.value < filteredItems.value.length || stageHasMore.value
);

const loadMoreLabel = computed(() => {
  if (isStageLoading.value) return t('PIPELINE.LOADING_ITEMS');
  if (remainingCount.value > 0) {
    return t('PIPELINE.LOAD_MORE', { remaining: remainingCount.value });
  }
  return t('PIPELINE.LOAD_MORE_SIMPLE');
});

// Clique no botão: primeiro esgota o reveal local (slice), depois pagina o
// stage no servidor (cursor).
// F22: `loadMoreFromAPI` volta cedo quando `stageHasMore` é falso — que é
// exatamente o estado de uma coluna que falhou antes de saber se há mais.
// Cross-review Codex: repete o MESMO request que falhou (append ou primeira
// página), marca o dispatch como stage-scoped para não acender o erro global
// do board, e trata a rejeição — `fetchStageCards` relança de propósito, e
// sem catch uma segunda falha viraria unhandled rejection.
const handleRetryStage = async () => {
  const pipelineId = store.getters['pipelines/getSelectedPipeline']?.id;
  if (!pipelineId || isStageLoading.value) return;

  try {
    await store.dispatch('pipelineCards/fetchStageCards', {
      pipelineId,
      stageId: props.id,
      append: stagePagination.value?.errorAppend || false,
      stageScoped: true,
    });
  } catch {
    // O erro já ficou registrado no próprio stage; a coluna segue oferecendo
    // "tentar de novo" em vez de estourar.
  }
};

const handleLoadMoreClick = () => {
  if (itemsToShowCount.value < filteredItems.value.length) {
    loadMoreItems();
  } else {
    loadMoreFromAPI();
  }
};

// Display count: prefere backend total quando não há filtros locais ativos.
// Audit 2026-04-25: cliente reportou que o badge mostrava só os carregados
// (50/500), enquanto o stage real tinha 2442 cards. Agora mostra o total
// real, e o filtro local fica refletido em filteredItems.length quando
// houver filtros aplicados.
const displayCount = computed(() => {
  if (!hasActiveFilters.value && backendStageCount.value !== null) {
    return backendStageCount.value;
  }
  return filteredItems.value.length;
});

// Tooltip detalhado mostrando carregados vs total para transparência.
const displayCountTooltip = computed(() => {
  const loaded = filteredItems.value.length;
  const total = backendStageCount.value;
  if (total === null || total === loaded) return null;
  return t('PIPELINE.COLUMN_OPTIONS.LOADED_OF_TOTAL', {
    loaded,
    total,
    defaultValue: `${loaded} carregados de ${total}`,
  });
});

// Empty-state message. Bugfix 2026-06-10: com filtros LOCAIS ativos e 0
// matches, a coluna ficava presa em "Carregando itens..." (o ternário antigo
// checava backendStageCount > 0 antes de hasActiveFilters, e stage_counts é
// cego aos filtros locais). Agora: carregando de verdade → LOADING; filtros
// ativos sem match → NO_FILTERED_ITEMS (com o botão "carregar mais" como
// escape manual para buscar matches nas próximas páginas do stage).
const emptyStateMessage = computed(() => {
  if (stageError.value) return t('PIPELINE.COLUMN_LOAD_FAILED');
  if (isStageLoading.value || store.state.pipelineCards.loading) {
    return t('PIPELINE.LOADING_ITEMS');
  }
  if (hasActiveFilters.value) return t('PIPELINE.NO_FILTERED_ITEMS');
  if (backendStageCount.value > 0) return t('PIPELINE.LOADING_ITEMS');
  return t('PIPELINE.NO_ITEMS');
});

// Auto-fetch when backend reports items exist but none are loaded locally.
// This covers edge cases where pagination/cache causes empty columns despite
// stage_counts showing items (the "Followup 4 → Nenhum Item" bug).
watch(
  () => [props.items.length, backendStageCount.value],
  ([localCount, backendCount]) => {
    if (
      localCount === 0 &&
      backendCount > 0 &&
      !store.state.pipelineCards.loading
    ) {
      loadMoreFromAPI();
    }
  }
);

// Resetar a contagem de itens visíveis quando item count changes.
// Na primeira carga após refresh, restaura o estado salvo em vez de resetar para 6.
// Mudanças subsequentes (real-time updates) não resetam a paginação.
watch(
  () => filteredItems.value.length,
  (newLength, oldLength) => {
    if (scrollStateToRestore && newLength > 0) {
      // Restaurar estado salvo (page refresh com scroll/paginação preservados)
      itemsToShowCount.value = Math.min(
        scrollStateToRestore.itemsToShowCount || 6,
        newLength
      );
      const savedScrollTop = scrollStateToRestore.scrollTop;
      scrollStateToRestore = null; // Restaurar apenas uma vez
      nextTick(() => {
        if (scrollContainer.value) {
          if (savedScrollTop) {
            scrollContainer.value.scrollTop = savedScrollTop;
          }
          updateScrollIndicator();
          if (
            scrollContainer.value.scrollHeight <=
            scrollContainer.value.clientHeight
          ) {
            loadMoreItems();
          }
        }
      });
    } else if (oldLength === 0 && newLength > 0) {
      // Primeira carga de itens: resetar para 6
      itemsToShowCount.value = 6;
      nextTick(() => {
        if (scrollContainer.value) {
          if (
            scrollContainer.value.scrollHeight <=
            scrollContainer.value.clientHeight
          ) {
            loadMoreItems();
          }
        }
      });
    }
    // Mudanças subsequentes (real-time add/remove) não resetam itemsToShowCount
  }
);

// Carregar configurações do localStorage (user-scoped)
onMounted(() => {
  // Load non-filter settings
  const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY.value);
  if (savedSettings) {
    columnSettings.value = JSON.parse(savedSettings);
  }
  const savedSort = localStorage.getItem(SORT_STORAGE_KEY.value);
  if (savedSort) {
    columnSort.value = JSON.parse(savedSort);
  }
  // Load filters from user-scoped storage key
  const savedFilters = localStorage.getItem(APPLIED_FILTER_STORAGE_KEY.value);
  if (savedFilters) {
    try {
      const parsed = JSON.parse(savedFilters);
      if (parsed && parsed.agent_id !== null && parsed.agent_id !== undefined) {
        parsed.agent_id = Number(parsed.agent_id);
      }
      if (typeof parsed === 'object' && parsed !== null) {
        columnFilters.value = parsed;
      }
    } catch (e) {
      // Error handled silently
    }
  }

  // Restaurar estado de scroll/paginação se não houver filtros ativos
  if (!columnFilters.value) {
    const savedScrollState = localStorage.getItem(
      SCROLL_STATE_STORAGE_KEY.value
    );
    if (savedScrollState) {
      try {
        const parsed = JSON.parse(savedScrollState);
        if (parsed?.itemsToShowCount) {
          if (filteredItems.value.length > 0) {
            // Itens já disponíveis: restaurar imediatamente
            itemsToShowCount.value = Math.min(
              parsed.itemsToShowCount,
              filteredItems.value.length
            );
            nextTick(() => {
              if (scrollContainer.value && parsed.scrollTop) {
                scrollContainer.value.scrollTop = parsed.scrollTop;
                updateScrollIndicator();
              }
            });
          } else {
            // Itens ainda carregando: o watcher vai restaurar quando chegarem
            scrollStateToRestore = parsed;
          }
        }
      } catch (e) {
        // Error handled silently
      }
    }
  }

  // Adicionar listener de scroll para lazy loading
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll);
    // Verificar se precisa carregar mais itens logo no início
    nextTick(() => {
      if (
        scrollContainer.value &&
        scrollContainer.value.scrollHeight <= scrollContainer.value.clientHeight
      ) {
        loadMoreItems();
      }
      updateScrollIndicator();
    });
  }

  // Fechar menu ao clicar fora
  document.addEventListener('click', closeOptionsMenuOnClickOutside);
  document.addEventListener('keydown', closeOptionsMenuOnEscape);

  // Listen for global dropdown close event
  emitter.on('closeAllPipelineDropdowns', handleCloseAllDropdowns);
});
onUnmounted(() => {
  document.removeEventListener('click', closeOptionsMenuOnClickOutside);
  document.removeEventListener('keydown', closeOptionsMenuOnEscape);
  emitter.off('closeAllPipelineDropdowns', handleCloseAllDropdowns);

  // Remover listener de scroll
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', handleScroll);
  }
  stopEdgeAutoScroll();
});
// Atualizar o watch para sincronizar as opções
watch(
  () => columnSettings.value.showAll,
  newValue => {
    if (newValue) {
      columnSettings.value.showWon = true;
      columnSettings.value.showLost = true;
    }
    saveColumnSettings();
  }
);

// Atualizar o watch para a opção "todos"
watch(
  [() => columnSettings.value.showWon, () => columnSettings.value.showLost],
  ([showWon, showLost]) => {
    columnSettings.value.showAll = showWon && showLost;
  }
);

const toggleOptionsMenu = () => {
  const wasOpen = showOptionsMenu.value;
  if (!wasOpen) {
    emitter.emit('closeAllPipelineDropdowns');
  }
  showOptionsMenu.value = !wasOpen;
  if (showOptionsMenu.value) {
    optionsPopoverRef.value?.show();
    nextTick(() => {
      focusFirstOptionsMenuItem();
    });
  } else {
    optionsPopoverRef.value?.hide();
  }
};

// Filtrar saiu do cabeçalho e virou item do menu: fecha o menu antes de abrir
// o modal, como os demais itens.
const handleFilterFromMenu = () => {
  showOptionsMenu.value = false;
  optionsPopoverRef.value?.hide();
  handleFilter();
};

const handleEditStage = () => {
  if (!canManagePipeline.value) return;
  editingStageForm.value = {
    name: props.title,
    color: props.color,
    description: props.description || '',
  };
  showEditStageModal.value = true;
  showOptionsMenu.value = false;
};

const reorderColumn = direction => {
  if (!canManagePipeline.value) return;
  emit('reorder', { columnId: props.id, direction });
  showOptionsMenu.value = false;
};

const handleOpenSortModal = () => {
  // Auditoria 2026-08-08 (F24): os controles persistem no `change`, então
  // "Cancelar" precisa de um ponto para onde voltar.
  sortSnapshot.value = { ...columnSort.value };
  showSortModal.value = true;
  showOptionsMenu.value = false;
};

const handleSaveStage = async () => {
  if (!canManagePipeline.value) return;
  const accountId = normalizedActiveAccountId();
  if (accountId === null) return;
  stageSaveGeneration += 1;
  const requestGeneration = stageSaveGeneration;
  try {
    const pipeline = store.getters['pipelines/getSelectedPipeline'];
    const pipelineId = pipeline?.id;
    if (!pipelineId) return;
    const requestIsCurrent = () =>
      requestGeneration === stageSaveGeneration &&
      accountId === normalizedActiveAccountId() &&
      String(pipelineId) ===
        String(store.getters['pipelines/getSelectedPipeline']?.id ?? '');
    const updatedStages = { ...pipeline.stages };

    updatedStages[props.id] = {
      ...updatedStages[props.id],
      name: editingStageForm.value.name,
      color: editingStageForm.value.color,
      description: editingStageForm.value.description,
    };

    await PipelinesAPI.update(pipelineId, { stages: updatedStages }, accountId);
    if (!requestIsCurrent()) return;
    showEditStageModal.value = false;

    // Força atualização do store
    await store.dispatch('pipelines/fetch', {
      force: true,
      accountId,
      generation: requestGeneration,
    });
    if (!requestIsCurrent()) return;

    // Emite evento para atualizar o pipeline
    emit('itemsUpdated');

    // Notifica sucesso
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.STAGE_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    if (
      requestGeneration !== stageSaveGeneration ||
      accountId !== normalizedActiveAccountId()
    ) {
      return;
    }
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.STAGE_UPDATED_ERROR'),
      action: { type: 'error' },
    });
  }
};

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    stageSaveGeneration += 1;
    showEditStageModal.value = false;
  },
  { flush: 'sync' }
);

// Recolher/Expandir todos os itens da coluna.
// Bug (report do usuário 2026-06-10): a versão antiga escrevia DIRETO no
// localStorage e emitia um evento que ninguém escuta — desde o refactor
// Card-Q1.2 os cards leem o ref reativo `collapsedItems` injetado pelo
// PipelineView, então nada re-renderizava. Agora passa pelo setter em lote
// provido pela view (1 write reativo + localStorage para N cards).
const toggleAllItems = () => {
  const target = !allItemsCollapsed.value;
  const entries = {};
  filteredItems.value.forEach(item => {
    entries[item.id] = target;
  });
  updateCollapsedItems(entries);

  // Fecha o menu de opções
  showOptionsMenu.value = false;
};
</script>

<template>
  <!-- Largura da coluna: quem manda é o wrapper em PipelineTab
       (`w-[13.5rem] min-w/max-w`), não esta section. O `flex-1` daqui era
       inerte — o `max-w` do pai impede qualquer distribuição — e o antigo
       `min-w-72` (288px) ainda forçava a section a estourar o wrapper.
       `min-w-0` deixa a section respeitar o wrapper.
       Densidade 2026-08-06: 256px por coluna (eram 288) mostram 5 colunas em
       1600px, contra 4. Medido a 1600px com 13 cards: 216px chegava a 6
       colunas mas cortava 11/13 nomes de card e 1/7 títulos de etapa; 256px
       zera o corte de título e o nome do card resolve o resto quebrando em
       2 linhas. -->
  <section
    role="region"
    :aria-label="
      t('PIPELINE.STAGE.LABEL') +
      ': ' +
      title +
      ', ' +
      displayCount +
      ' ' +
      (displayCount === 1 ? t('PIPELINE.CARD_SINGULAR') : t('PIPELINE.CARDS'))
    "
    class="pipeline-column flex h-full w-full min-w-0 flex-col rounded-xl border border-t-[3px] shadow-sm"
    :class="columnColorClasses.column"
  >
    <header class="rounded-t-xl border-b border-n-weak px-2.5 pb-2 pt-2">
      <!-- Layout (report do usuário 2026-06-10): título trunca (min-w-0 +
           truncate) e o bloco de ações tem shrink-0 — antes, título longo +
           total R$ espremiam o botão ⋮ contra a borda da coluna. -->
      <div class="flex items-center justify-between gap-1.5">
        <div class="flex items-center gap-1.5 min-w-0 flex-1">
          <h2
            :id="`column-title-${id}`"
            class="text-xs font-semibold truncate"
            :class="columnColorClasses.text"
            :title="title"
          >
            {{ title }}
          </h2>
          <span
            v-tooltip="
              displayCountTooltip
                ? {
                    content: displayCountTooltip,
                    delay: { show: 300, hide: 0 },
                    placement: 'top',
                  }
                : null
            "
            class="inline-flex items-center justify-center min-w-5 h-5 px-1 text-xs font-medium rounded-full shrink-0"
            :class="[
              columnColorClasses.count,
              { 'cursor-help': displayCountTooltip },
            ]"
          >
            {{ displayCount }}
          </span>
          <!-- Indicador de filtro ativo -->
          <span
            v-if="hasActiveFilters"
            class="w-1.5 h-1.5 rounded-full inline-block shrink-0"
            :class="columnColorClasses.dot"
          />
        </div>
        <!-- Densidade 2026-08-06: a 216px de coluna, três botões consumiam
             ~92 dos 196px úteis e o nome da etapa truncava ("Pré-Aten…",
             "Consulta …"). Filtrar desceu para o menu de opções; ficam
             visíveis só adicionar (ação frequente) e o menu. -->
        <div class="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            :aria-label="t('PIPELINE.ADD_CARD')"
            data-tour="add-card-button"
            class="size-11 fine-pointer:size-7 flex items-center justify-center rounded-md text-woot-500 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/20 hover:bg-woot-100 dark:hover:bg-woot-900/40 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
            @click="$emit('add', id)"
          >
            <fluent-icon icon="add" size="14" />
          </button>
          <!-- Botão de mais opções -->
          <div class="relative">
            <Popover
              ref="optionsPopoverRef"
              align="end"
              disable-mobile-view
              :show-content-border="false"
              @hide="showOptionsMenu = false"
            >
              <button
                ref="optionsButtonRef"
                type="button"
                :aria-label="t('PIPELINE.COLUMN_OPTIONS.TITLE')"
                :aria-expanded="showOptionsMenu"
                :aria-controls="fieldId('options-menu')"
                aria-haspopup="menu"
                class="options-menu-button size-11 fine-pointer:size-7 flex items-center justify-center rounded-md text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-n-brand"
                @click.stop="toggleOptionsMenu"
              >
                <fluent-icon icon="more-vertical" size="14" />
              </button>

              <!-- Menu de Opções -->
              <template #content>
                <div
                  v-if="showOptionsMenu"
                  :id="fieldId('options-menu')"
                  ref="optionsMenuRef"
                  role="menu"
                  :aria-label="t('PIPELINE.COLUMN_OPTIONS.TITLE')"
                  class="options-menu-content w-48 overflow-y-auto rounded-xl bg-n-solid-3 py-2 shadow-lg outline outline-1 outline-n-weak"
                  @click.stop
                  @keydown="handleOptionsMenuKeydown"
                  @focusout="closeOptionsMenuAfterFocusLeaves"
                >
                  <div class="px-4 py-2">
                    <button
                      type="button"
                      role="menuitem"
                      class="flex min-h-11 items-center gap-2 text-sm text-n-slate-11 w-full hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand"
                      @click="handleFilterFromMenu"
                    >
                      <fluent-icon icon="filter" size="16" />
                      <span>{{ t('PIPELINE.FILTER_ITEMS') }}</span>
                    </button>
                  </div>
                  <div
                    v-if="canManagePipeline"
                    class="border-t border-n-weak my-1"
                  />
                  <div v-if="canManagePipeline" class="px-4 py-2">
                    <button
                      type="button"
                      role="menuitem"
                      class="flex min-h-11 items-center gap-2 text-sm text-n-slate-11 w-full hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand"
                      @click="handleEditStage"
                    >
                      <fluent-icon icon="edit" size="16" />
                      <span>{{ t('PIPELINE.COLUMN_OPTIONS.EDIT_STAGE') }}</span>
                    </button>
                  </div>
                  <div
                    v-if="canManagePipeline"
                    class="border-t border-n-weak my-1"
                  />
                  <div v-if="canManagePipeline" class="px-4 py-1">
                    <button
                      type="button"
                      role="menuitem"
                      :disabled="!canMovePrevious"
                      class="flex min-h-11 w-full items-center gap-2 text-sm text-n-slate-11 hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-40"
                      @click="reorderColumn(-1)"
                    >
                      <fluent-icon icon="arrow-left" size="16" />
                      <span>{{
                        t('PIPELINE.COLUMN_OPTIONS.MOVE_STAGE_LEFT')
                      }}</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      :disabled="!canMoveNext"
                      class="flex min-h-11 w-full items-center gap-2 text-sm text-n-slate-11 hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-40"
                      @click="reorderColumn(1)"
                    >
                      <fluent-icon icon="arrow-right" size="16" />
                      <span>{{
                        t('PIPELINE.COLUMN_OPTIONS.MOVE_STAGE_RIGHT')
                      }}</span>
                    </button>
                  </div>
                  <div class="border-t border-n-weak my-1" />
                  <div class="px-4 py-2">
                    <label
                      class="flex min-h-11 items-center gap-2 text-sm text-n-slate-11"
                    >
                      <input
                        :id="fieldId('show-all')"
                        v-model="columnSettings.showAll"
                        name="show_all"
                        type="checkbox"
                        class="rounded border-n-weak text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50"
                        @change="saveColumnSettings"
                      />
                      <span>{{ t('PIPELINE.COLUMN_OPTIONS.SHOW_ALL') }}</span>
                    </label>
                  </div>
                  <!-- Só mostra opções de ganho/perdido se habilitado -->
                  <template v-if="enableWonLostStages">
                    <div class="px-4 py-2">
                      <label
                        class="flex min-h-11 items-center gap-2 text-sm text-n-slate-11"
                      >
                        <input
                          :id="fieldId('show-won')"
                          v-model="columnSettings.showWon"
                          name="show_won"
                          type="checkbox"
                          class="rounded border-n-weak text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50"
                          @change="saveColumnSettings"
                        />
                        <span>{{ t('PIPELINE.COLUMN_OPTIONS.SHOW_WON') }}</span>
                      </label>
                    </div>
                    <div class="px-4 py-2">
                      <label
                        class="flex min-h-11 items-center gap-2 text-sm text-n-slate-11"
                      >
                        <input
                          :id="fieldId('show-lost')"
                          v-model="columnSettings.showLost"
                          name="show_lost"
                          type="checkbox"
                          class="rounded border-n-weak text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50"
                          @change="saveColumnSettings"
                        />
                        <span>{{
                          t('PIPELINE.COLUMN_OPTIONS.SHOW_LOST')
                        }}</span>
                      </label>
                    </div>
                  </template>
                  <div class="px-4 py-2">
                    <label
                      class="flex min-h-11 items-center gap-2 text-sm text-n-slate-11"
                    >
                      <input
                        :id="fieldId('hide-column')"
                        v-model="columnSettings.hideColumn"
                        name="hide_column"
                        type="checkbox"
                        class="rounded border-n-weak text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50"
                        @change="saveColumnSettings"
                      />
                      <span>{{ t('PIPELINE.COLUMN_OPTIONS.HIDE_STAGE') }}</span>
                    </label>
                  </div>
                  <div class="px-4 py-2">
                    <button
                      type="button"
                      role="menuitem"
                      class="flex min-h-11 items-center gap-2 text-sm text-n-slate-11 w-full hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand"
                      @click="handleOpenSortModal"
                    >
                      <fluent-icon icon="arrow-sort" size="16" />
                      <span>{{ t('PIPELINE.COLUMN_OPTIONS.SORT') }}</span>
                    </button>
                  </div>
                  <div class="border-t border-n-weak my-1" />
                  <div class="px-4 py-2">
                    <button
                      type="button"
                      role="menuitem"
                      class="flex min-h-11 items-center gap-2 text-sm text-n-slate-11 w-full hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand"
                      @click="toggleAllItems"
                    >
                      <fluent-icon
                        :icon="
                          allItemsCollapsed ? 'arrow-expand' : 'arrow-outwards'
                        "
                        size="16"
                      />
                      <span>{{
                        allItemsCollapsed
                          ? t('PIPELINE.COLUMN_OPTIONS.EXPAND_ALL')
                          : t('PIPELINE.COLUMN_OPTIONS.COLLAPSE_ALL')
                      }}</span>
                    </button>
                  </div>
                </div>
              </template>
            </Popover>
          </div>
        </div>
      </div>
      <!-- Total em linha própria: com os alvos de toque de 44px dos três
             botões, manter o valor na mesma linha consumia ~210px dos 256px
             úteis de uma coluna de 288px e o título truncava para nada quando
             o funil tinha etapas demais (report 2026-08-05). -->
      <div v-if="formattedTotal" class="mt-0.5 flex justify-end">
        <span
          v-tooltip="{
            content: columnTotalTooltip,
            delay: { show: 300, hide: 0 },
            placement: 'top',
          }"
          class="text-[11px] font-semibold cursor-help whitespace-nowrap tabular-nums"
          :class="columnColorClasses.text"
        >
          {{ displayedTotal }}
        </span>
      </div>
    </header>

    <!-- Scroll indicator gradient (topo). `-mb-6` para sobrepor o início da
         lista sem ocupar altura própria — mesma técnica do gradiente de baixo. -->
    <div
      v-if="canScrollUp"
      class="pointer-events-none relative z-10 -mb-6 h-6 bg-gradient-to-b from-n-solid-2/80 to-transparent"
    />

    <div
      ref="scrollContainer"
      class="relative m-0.5 min-h-0 flex-1 overflow-y-auto rounded-b-xl transition-colors duration-200 motion-reduce:transition-none [scrollbar-color:rgb(148_163_184/0.3)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-slate-400/30 hover:[&::-webkit-scrollbar-thumb]:bg-slate-400/50 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
      :class="
        isDragOver
          ? 'border-2 border-dashed border-n-brand bg-n-alpha-1'
          : 'border-2 border-transparent'
      "
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
      @dragend="stopEdgeAutoScroll"
    >
      <!-- Drop indicator -->
      <div
        v-if="isDragOver"
        class="h-0.5 bg-n-brand rounded mx-3 mt-2 animate-pulse motion-reduce:animate-none"
      />
      <div class="px-4">
        <div
          role="list"
          :aria-labelledby="`column-title-${id}`"
          class="py-2 space-y-2.5"
        >
          <template v-if="displayedItems.length">
            <PipelineCard
              v-for="(item, index) in displayedItems"
              :key="item.id"
              :card="item"
              :stage-name="title"
              :aria-posinset="index + 1"
              :aria-setsize="displayedItems.length"
              draggable
              @open-details="modifiedItem => $emit('itemClick', modifiedItem)"
              @edit="modifiedItem => $emit('edit', modifiedItem)"
              @delete="$emit('delete', item)"
              @move="$emit('drop', $event)"
              @status-change="handleStatusChange"
            />
            <!-- Load more indicator (local slice reveal + server-side cursor) -->
            <div v-if="showLoadMoreButton" role="listitem">
              <button
                type="button"
                :disabled="isStageLoading"
                class="min-h-11 w-full py-2 text-xs font-medium text-n-slate-11 hover:text-n-brand bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg focus-visible:outline-2 focus-visible:outline-n-brand transition-colors disabled:opacity-60 disabled:cursor-wait"
                @click="handleLoadMoreClick"
              >
                {{ loadMoreLabel }}
              </button>
            </div>
          </template>
          <template v-else>
            <div
              role="listitem"
              class="flex items-center justify-center h-24 text-body-main text-n-slate-10"
            >
              <span role="status" aria-live="polite">
                {{ emptyStateMessage }}
              </span>
            </div>
            <!-- F22: a coluna falhou. Sem isto o usuário via "carregando"
                 indefinidamente, sem forma de tentar de novo. -->
            <div v-if="stageError" role="listitem">
              <button
                type="button"
                :disabled="isStageLoading"
                class="min-h-11 w-full py-2 text-xs font-medium text-n-ruby-11 hover:text-n-ruby-12 bg-n-ruby-3 hover:bg-n-ruby-4 rounded-lg focus-visible:outline-2 focus-visible:outline-n-brand transition-colors disabled:opacity-60 disabled:cursor-wait"
                @click="handleRetryStage"
              >
                {{ t('PIPELINE.RETRY_COLUMN') }}
              </button>
            </div>
            <!-- Coluna vazia localmente mas com cards no servidor (fallback de
                 cache global, falha parcial de stage): oferece o fetch da
                 primeira página em vez de um beco sem saída (achado Codex). -->
            <div v-else-if="showLoadMoreButton" role="listitem">
              <button
                type="button"
                :disabled="isStageLoading"
                class="min-h-11 w-full py-2 text-xs font-medium text-n-slate-11 hover:text-n-brand bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg focus-visible:outline-2 focus-visible:outline-n-brand transition-colors disabled:opacity-60 disabled:cursor-wait"
                @click="handleLoadMoreClick"
              >
                {{ loadMoreLabel }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Scroll indicator gradient -->
    <div
      v-if="canScrollDown"
      class="h-6 pointer-events-none rounded-b-xl -mt-6 relative z-10 bg-gradient-to-t from-n-solid-2/80 to-transparent"
    />

    <!-- Modal de filtro (montagem lazy: o componente despacha agents/labels
         no onMounted; eager em cada coluna = N fetches no load do board) -->
    <PipelineFilter
      v-if="showFilterModal"
      :show="showFilterModal"
      :filtered-results="filteredResults"
      :current-pipeline="store.getters['pipelines/getSelectedPipeline']"
      :initial-filters="columnFilters"
      scope="column"
      :column-name="title"
      size="large"
      @close="showFilterModal = false"
      @apply="handleFilterApply"
    />

    <!-- Modal de Edição de Etapa -->
    <Modal
      v-model:show="showEditStageModal"
      @close="() => (showEditStageModal = false)"
    >
      <div class="p-4 sm:p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.EDIT_STAGE_MODAL.TITLE') }}
        </h3>

        <div class="space-y-4">
          <div>
            <label
              :for="fieldId('stage-name')"
              class="block text-sm font-medium mb-1"
              >{{ t('PIPELINE.EDIT_STAGE_MODAL.NAME') }}</label
            >
            <Input
              :id="fieldId('stage-name')"
              v-model="editingStageForm.name"
              name="stage_name"
              type="text"
              class="w-full"
              custom-input-class="!h-11"
              :placeholder="t('PIPELINE.EDIT_STAGE_MODAL.NAME_PLACEHOLDER')"
            />
          </div>

          <div>
            <!-- ColorPicker é botão custom (não-labelable): span + group
                 aria-labelledby no lugar de <label> órfão. -->
            <span
              :id="fieldId('stage-color-label')"
              class="block text-sm font-medium mb-1"
              >{{ t('PIPELINE.EDIT_STAGE_MODAL.COLOR') }}</span
            >
            <div role="group" :aria-labelledby="fieldId('stage-color-label')">
              <StageColorPicker v-model="editingStageForm.color" />
            </div>
          </div>

          <div>
            <label
              :for="fieldId('stage-description')"
              class="block text-sm font-medium mb-1"
              >{{ t('PIPELINE.EDIT_STAGE_MODAL.DESCRIPTION') }}</label
            >
            <TextArea
              :id="fieldId('stage-description')"
              v-model="editingStageForm.description"
              class="w-full"
              :placeholder="
                t('PIPELINE.EDIT_STAGE_MODAL.DESCRIPTION_PLACEHOLDER')
              "
            />
          </div>

          <div class="flex flex-col-reverse justify-end gap-2 sm:flex-row">
            <button
              type="button"
              class="min-h-11 rounded-lg px-4 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
              @click="showEditStageModal = false"
            >
              {{ t('PIPELINE.ACTIONS.CANCEL') }}
            </button>
            <button
              type="button"
              class="min-h-11 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-n-brand/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-blue-9"
              @click="handleSaveStage"
            >
              {{ t('PIPELINE.ACTIONS.SAVE') }}
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Modal de Ordenação -->
    <Modal v-model:show="showSortModal" @close="() => (showSortModal = false)">
      <div class="p-4 sm:p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.SORT_MODAL.TITLE') }}
        </h3>

        <div class="space-y-4">
          <div>
            <label
              :for="fieldId('sort-type')"
              class="block text-sm font-medium mb-2"
              >{{ t('PIPELINE.SORT_MODAL.SORT_BY') }}</label
            >
            <Select
              v-model="columnSort.type"
              :input-id="fieldId('sort-type')"
              name="sort_type"
              class="w-full [&>select]:w-full"
              :options="sortSelectOptions"
              :aria-label="t('PIPELINE.SORT_MODAL.SORT_BY')"
              @update:model-value="saveSortSettings"
            />
          </div>

          <div>
            <!-- Grupo de radios: span + radiogroup aria-labelledby no lugar
                 de <label> órfão (não há controle único para associar). -->
            <span
              :id="fieldId('sort-direction-label')"
              class="block text-sm font-medium mb-2"
              >{{ t('PIPELINE.SORT_MODAL.DIRECTION') }}</span
            >
            <div
              role="radiogroup"
              :aria-labelledby="fieldId('sort-direction-label')"
              class="flex gap-4"
            >
              <label class="flex min-h-11 items-center gap-2">
                <input
                  :id="fieldId('sort-direction-asc')"
                  v-model="columnSort.direction"
                  :name="fieldId('sort-direction')"
                  type="radio"
                  value="asc"
                  @change="saveSortSettings"
                />
                <span class="text-sm">{{
                  t('PIPELINE.SORT_MODAL.ASCENDING')
                }}</span>
              </label>
              <label class="flex min-h-11 items-center gap-2">
                <input
                  :id="fieldId('sort-direction-desc')"
                  v-model="columnSort.direction"
                  :name="fieldId('sort-direction')"
                  type="radio"
                  value="desc"
                  @change="saveSortSettings"
                />
                <span class="text-sm">{{
                  t('PIPELINE.SORT_MODAL.DESCENDING')
                }}</span>
              </label>
            </div>
          </div>

          <div class="flex flex-col-reverse justify-end gap-2 sm:flex-row">
            <button
              type="button"
              class="min-h-11 rounded-lg px-4 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
              @click="cancelSortSettings"
            >
              {{ t('PIPELINE.FORM.CANCEL') }}
            </button>
            <button
              type="button"
              class="min-h-11 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-n-brand/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-blue-9"
              @click="showSortModal = false"
            >
              {{ t('PIPELINE.FORM.APPLY') }}
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Deal status: marcar Ganho/Perdido a partir do dropdown inline do card.
         Os modais capturam won_value/motivo (alguns funis exigem) e chamam os
         endpoints de deal_status; o sucesso aplica o payload no store. -->
    <MarkAsWonModal
      v-if="dealStatusCard"
      :item="dealStatusCard"
      :show="showWonModal"
      @close="showWonModal = false"
      @success="handleDealStatusSuccess"
    />
    <MarkAsLostModal
      v-if="dealStatusCard"
      :item="dealStatusCard"
      :show="showLostModal"
      @close="showLostModal = false"
      @success="handleDealStatusSuccess"
    />
  </section>
</template>

<!-- CSS migrated to Tailwind classes -->
